import { Navigate, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';
import { useAuthSession } from './useAuth.ts';

/**
 * Wrap any authenticated route in <RequireAuth>...</RequireAuth>.
 * - While the session is loading: render nothing (avoids a flicker
 *   redirect to /signin and back).
 * - When the user is not signed in: redirect to /signin, capturing the
 *   intended destination in `?next=` so the auth flow can return them
 *   after sign-in.
 * - When the user is signed in: render children.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: session, isLoading } = useAuthSession();
  const location = useLocation();

  if (isLoading) return null;

  if (!session) {
    const nextHref = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?next=${encodeURIComponent(nextHref)}`} replace />;
  }

  return <>{children}</>;
}
